import React from 'react';
import { makeStyles } from 'tss-react/mui';

import { ContentfulImg } from 'ha/modules/Contentful/components/ContentfulImg';
import { Container, Grid, Link, Paper, Typography, units } from '@hbf/dsl';
import { LazyComponent } from 'ha/components.legacy/LazyComponent';
import { useSelector } from 'react-redux';
import { getCurrentLanguageCode } from 'ha/modules/LanguageSwitcher/selectors';

import { useIntl } from 'ha/i18n';
import { HermesData } from '../types';

interface Props {
  data: HermesData;
}

const useStyles = makeStyles()(theme => ({
  container: {
    padding: units.yx(2, 0),
    [theme.breakpoints.up('md')]: {
      padding: units.yx(3, 0),
    },
  },
  card: {
    maxWidth: units.rem(18.75),
  },
  paper: {
    paddingBottom: units.rem(1),
  },
  title: {
    fontSize: units.rem(1.5),
    lineHeight: 1.29,
    marginBottom: units.rem(0.5),
    [theme.breakpoints.up('md')]: {
      lineHeight: 1.23,
      fontSize: units.rem(1.85),
      marginBottom: units.rem(2.8),
    },
  },
  imageWrapper: {
    position: 'relative',
    height: units.rem(15.25),
    marginBottom: units.rem(1),
  },
  image: {
    position: 'absolute',
    objectFit: 'cover',
    height: '100%',
    width: '100%',
  },
  limitedText: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
  },
  blogTitle: {
    padding: units.yx(0, 1),
    marginBottom: units.rem(0.5),
  },
  blogDescription: {
    padding: units.yx(0, 1),
  },
}));

export const BlogCards: React.FC<Props> = ({ data }) => {
  const { urlResolver } = useIntl();
  const { classes, cx } = useStyles();
  const lang = useSelector(getCurrentLanguageCode);

  const renderTitle = React.useMemo(() => {
    const title = data.promotedContentTitle;

    if (!data.slug) {
      return title;
    }

    return (
      <Link to={urlResolver.formatHrefURL(`/${data.slug}`)} title={title}>
        {title}
      </Link>
    );
  }, [data.promotedContentTitle, data.slug, urlResolver]);

  return (
    <div className={classes.container}>
      <Container>
        {data.promotedContentTitle ? (
          <Grid container justifyContent="center">
            <Grid item xs={12}>
              <Typography.H3
                className={classes.title}
                component="h3"
                align="center"
                data-test-locator="BlogCards Section Title"
              >
                {renderTitle}
              </Typography.H3>
            </Grid>
          </Grid>
        ) : null}

        <Grid container justifyContent="center" spacing={3}>
          {data.promotedContent!.map(blog => {
            const headerImage = blog.headerImageAsset || {
              fields: { url: blog.headerImage },
            };
            const desc = headerImage.fields.description;
            const alt = desc ? desc[lang] || desc.en : '';
            return (
              <Grid item key={blog.pathname} className={classes.card}>
                <Link to={blog.pathname}>
                  <Paper className={classes.paper}>
                    <Grid
                      container
                      alignItems="flex-end"
                      justifyContent="center"
                      className={classes.imageWrapper}
                      data-test-locator="BlogCards Image"
                    >
                      <LazyComponent bottomOffset="-50%">
                        <ContentfulImg
                          src={headerImage.fields.url}
                          alt={alt}
                          className={classes.image}
                          role="presentation"
                        />
                      </LazyComponent>
                    </Grid>

                    <Typography.H3
                      className={cx(classes.limitedText, classes.blogTitle)}
                      component="h4"
                      data-test-locator="BlogCards Title"
                    >
                      {blog.title}
                    </Typography.H3>
                    <Typography.Paragraph
                      className={cx(
                        classes.limitedText,
                        classes.blogDescription,
                      )}
                      component="p"
                      data-test-locator="BlogCards Description"
                    >
                      {blog.description}
                    </Typography.Paragraph>
                  </Paper>
                </Link>
              </Grid>
            );
          })}
        </Grid>
      </Container>
    </div>
  );
};
